var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/a-user.ts
import { Arg, Entity, Field, Mutation, Query } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { registerMutation, registerQuery } from "@adornis/baseql/metadata/register.js";
import { context } from "@adornis/baseql/server/context.js";
import { validate } from "@adornis/validation/decorators.js";
import { and } from "@adornis/validation/functions/and.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import { _subscribeCurrentUser } from "../client/currentUser.js";
import { impersonate, stopImpersonation } from "../client/impersonation.js";
import { impersonateUserResolver, stopImpersonationOfUserResolver } from "../server/resolver/impersonation.js";
import {
  downGradeSessionResolver,
  loginWithPasswordResolver,
  loginWithTokenResolver
} from "../server/resolver/login.js";
import {
  changePasswordWithTokenResolver,
  checkPasswordChangeTokenResolver,
  generatePasswordChangeTokenResolver,
  isUserPasswordSetResolver
} from "../server/resolver/password.js";
import { registerUserResolver } from "../server/resolver/register.js";
import { addRoleToUserResolver, removeRoleFromUserResolver } from "../server/resolver/roles.js";
import { ANY_CONTEXT, GLOBAL_CONTEXT } from "./a-roles.js";
import { CurrentUserInfo } from "./currentUserInfo.js";
import { NoPermissionError } from "./errors.js";
import * as names from "./names.js";
import { AdornisRoleToContextsWrapper } from "./roleToContextsWrapper.js";
import { UserPermission } from "./types.js";
import { emailValidation, nameValidation, passwordValidation, validatePassword } from "./validators.js";
var BaseUser = class extends AdornisEntity {
};
BaseUser._class = names.baseUserClassName;
__decorateClass([
  validate(and(nonOptional(), nameValidation)),
  Field((type) => String)
], BaseUser.prototype, "username", 2);
__decorateClass([
  validate(emailValidation),
  Field((type) => String)
], BaseUser.prototype, "email", 2);
__decorateClass([
  validate(passwordValidation),
  Field((type) => String)
], BaseUser.prototype, "password", 2);
BaseUser = __decorateClass([
  Entity()
], BaseUser);
var AdornisUser = class extends MongoEntity {
  constructor(doc) {
    super(doc);
  }
  static async beforeUpdate(oldEntity, update) {
    const rolesEqual = (oldRoles, newRoles) => {
      if (newRoles.length !== oldRoles.length)
        return false;
      for (const role of newRoles) {
        const oldRole = oldRoles.find((r) => r.name === role.name);
        if (!oldRole || role.contexts.sort().join() !== oldRole.contexts.sort().join())
          return false;
      }
      return true;
    };
    if (update.has("roles") && !rolesEqual(oldEntity.roles, update.roles) && !context.serverContext) {
      const actingUser = await CurrentUserInfo.getMyself()({ roles: AdornisRoleToContextsWrapper.allFields });
      if (!actingUser)
        throw new NoPermissionError({ permission: UserPermission.ChangeRoles, context: oldEntity._id });
      actingUser.assertPermission(UserPermission.ChangeRoles, oldEntity._id);
    }
  }
  /**
   * Impersonate the user with the id userId
   * @param userID userID to be impersonated
   * @param userClass _class of the user to be impersonated
   * @throws if permission not granted
   *
   * @deprecated use standalone function `impersonate` exported from `\@adornis/users/client/impersonation`
   */
  static async impersonate(userID, userClass = this._class) {
    return impersonate(userID, userClass);
  }
  /**
   * Stop impersonation if impersonated
   * @throws if not impersonated
   *
   * @deprecated use standalone function `stopImpersonation` exported from `\@adornis/users/client/impersonation`
   */
  static async stopImpersonation() {
    return stopImpersonation();
  }
  /**
   * Change password for given userId
   * @param userId userId
   * @param password new password
   * @throws if permission not granted
   *
   * @deprecated use standalone function
   */
  static async changePassword(userID, password) {
    return changePassword(userID, password, this);
  }
  /**
   * Checks if user has the permission in given context
   * @param permission permission to check
   * @param context context for the permission, have a look at a-roles for more detail
   * @returns true if user has permission, false if not
   */
  hasPermission(permission, context2 = ANY_CONTEXT) {
    if (context2 === ANY_CONTEXT) {
      return this.roles.some((role) => role.permissions.includes(permission));
    }
    return this.roles.some(
      (role) => (role.contexts.includes(context2) || role.contexts.includes(GLOBAL_CONTEXT)) && role.permissions.includes(permission)
    );
  }
  /**
   * assert that this user has a certain permission on a certian context.
   *
   * @param permission permission to check for
   * @param context context to check for
   * @throws if permission is not granted
   * @returns void
   */
  assertPermission(permission, context2) {
    if (!this.hasPermission(permission, context2))
      throw new NoPermissionError({ permission, context: context2 });
  }
  /**
   * Remove a role from this user.
   *
   * @param role role name to remove
   * @param contexts contexts to remove the role from
   * @throws if user has no permission to change roles
   * @returns true if the user was modified, false if not
   */
  async removeRole(role, contexts) {
    return removeRoleFromUser(this._id, role, contexts, this.constructor)();
  }
  /**
   * Add a role to this user.
   *
   * @param role role name to add
   * @param contexts contexts to add the role in
   * @throws if user has no permission to change roles
   * @returns true if the user was modified, false if not
   */
  async addRole(role, contexts) {
    return addRoleToUser(this._id, role, contexts, this.constructor)();
  }
  static loginWithToken(token) {
    return loginWithTokenResolver(token);
  }
  static loginWithPassword(usernameOrMail, password) {
    return loginWithPasswordResolver(usernameOrMail, password, this._class);
  }
  static downGradeSession() {
    return downGradeSessionResolver();
  }
  static impersonateUser(userID, userClass = this._class) {
    return impersonateUserResolver(userID, userClass);
  }
  static stopImpersonationOfUser() {
    return stopImpersonationOfUserResolver();
  }
  static generatePasswordChangeToken(userID) {
    return generatePasswordChangeTokenResolver(userID, this._class);
  }
  static changePasswordWithToken(token, newPassword, login) {
    return changePasswordWithTokenResolver(token, newPassword, login);
  }
  static checkPasswordChangeToken(token) {
    return checkPasswordChangeTokenResolver(token);
  }
  static isPasswordSet(userID) {
    return isUserPasswordSetResolver(userID, this._class);
  }
  // #endregion
  // #region register
  /**
   * Registers a new user
   *
   * Constructs the user object on the client and passes it to `requestRegisterUser`
   *
   * @param username Username of the new user
   * @param email Email of the new user
   * @param password Password of the new user
   * @param additionalFields a plain object containing any fields other than username and email on your user class, even nested. Use this to set custom fields on creation
   * @param loginAfterCreate whether to login the current session with the new user if creation succeeds
   * @throws if permission not granted
   * @returns userID of the new user
   *
   * @deprecated use standalone function
   */
  static async registerUser({
    username = "",
    email,
    password,
    additionalFields,
    loginAfterCreate = false
  }) {
    return registerUser({ username, email, password, additionalFields, loginAfterCreate, userClass: this });
  }
  static requestRegisterUser(userObj, password, loginAfterCreate = false) {
    return registerUserResolver(userObj, password, loginAfterCreate);
  }
  static addRoleToUser(userID, roleName, contexts) {
    return addRoleToUserResolver(this._class, userID, roleName, contexts);
  }
  static removeRoleFromUser(userID, roleName, contexts) {
    return removeRoleFromUserResolver(this._class, userID, roleName, contexts);
  }
  // #endregion
  // #endregion
};
AdornisUser._collectionName = names.adornisUserCollectionName;
AdornisUser._class = names.adornisUserClassName;
AdornisUser.usernameValidator = and(nonOptional(), nameValidation);
AdornisUser.emailValidator = emailValidation;
AdornisUser.passwordValidator = validatePassword;
__decorateClass([
  validate((options) => constructValue(options.target).constructor.usernameValidator(options)),
  Field((type) => String)
], AdornisUser.prototype, "username", 2);
__decorateClass([
  validate((options) => constructValue(options.target).constructor.emailValidator(options)),
  Field((type) => String)
], AdornisUser.prototype, "email", 2);
__decorateClass([
  Field((type) => [AdornisRoleToContextsWrapper], { default: (v) => v ?? [] })
], AdornisUser.prototype, "roles", 2);
__decorateClass([
  Field((type) => Boolean, { default: (v) => v ?? false })
], AdornisUser.prototype, "isOnline", 2);
__decorateClass([
  Field((type) => Date, { default: (v) => v ?? /* @__PURE__ */ new Date() })
], AdornisUser.prototype, "createdAt", 2);
__decorateClass([
  Field((type) => String, { default: (x) => x ?? "" })
], AdornisUser.prototype, "ios_sub", 2);
__decorateClass([
  Field((type) => Boolean, { default: (v) => v ?? false })
], AdornisUser.prototype, "emailConfirmed", 2);
__decorateClass([
  Mutation((type) => [String]),
  __decorateParam(0, Arg("token", (type) => String))
], AdornisUser, "loginWithToken", 1);
__decorateClass([
  Mutation((type) => [String]),
  __decorateParam(0, Arg("usernameOrMail", (type) => String)),
  __decorateParam(1, Arg("password", (type) => String))
], AdornisUser, "loginWithPassword", 1);
__decorateClass([
  Mutation((type) => Boolean)
], AdornisUser, "downGradeSession", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userID", (type) => String)),
  __decorateParam(1, Arg("userClass", (type) => String))
], AdornisUser, "impersonateUser", 1);
__decorateClass([
  Mutation((type) => Boolean)
], AdornisUser, "stopImpersonationOfUser", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userID", (type) => String))
], AdornisUser, "generatePasswordChangeToken", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("token", (type) => String)),
  __decorateParam(1, Arg("newPassword", (type) => String)),
  __decorateParam(2, Arg("login", (type) => Boolean))
], AdornisUser, "changePasswordWithToken", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("token", (type) => String))
], AdornisUser, "checkPasswordChangeToken", 1);
__decorateClass([
  Query((type) => Boolean),
  __decorateParam(0, Arg("userID", (type) => String))
], AdornisUser, "isPasswordSet", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userObj", (type) => AdornisUser)),
  __decorateParam(1, Arg("password", (type) => String)),
  __decorateParam(2, Arg("loginAfterCreate", (type) => Boolean))
], AdornisUser, "requestRegisterUser", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("userID", (type) => String)),
  __decorateParam(1, Arg("roleName", (type) => String)),
  __decorateParam(2, Arg("contexts", (type) => [String]))
], AdornisUser, "addRoleToUser", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("userID", (type) => String)),
  __decorateParam(1, Arg("roleName", (type) => String)),
  __decorateParam(2, Arg("contexts", (type) => [String]))
], AdornisUser, "removeRoleFromUser", 1);
AdornisUser = __decorateClass([
  Entity()
], AdornisUser);
var loginWithToken = registerMutation({
  operationName: "loginWithToken",
  resolve: loginWithTokenResolver,
  type: () => [String],
  params: [{ name: "token", type: () => String }]
});
var loginWithPasswordOp = registerMutation({
  operationName: "loginWithPassword",
  resolve: loginWithPasswordResolver,
  type: () => [String],
  params: [
    { name: "usernameOrMail", type: () => String },
    { name: "password", type: () => String },
    { name: "userClass", type: () => String }
  ]
});
var loginWithPassword = (usernameOrEmail, password, userClass = AdornisUser) => loginWithPasswordOp(usernameOrEmail, password, userClass._class);
var downGradeSession = registerMutation({
  operationName: "downGradeSession",
  resolve: downGradeSessionResolver,
  type: () => Boolean
});
var impersonateUser = registerMutation({
  operationName: "impersonateUser",
  resolve: impersonateUserResolver,
  type: () => String,
  params: [
    { name: "userID", type: () => String },
    { name: "userClassName", type: () => String }
  ]
});
var stopImpersonationOfUser = registerMutation({
  operationName: "stopImpersonationOfUser",
  resolve: stopImpersonationOfUserResolver,
  type: () => Boolean
});
var generatePasswordChangeTokenOp = registerMutation({
  operationName: "generatePasswordChangeToken",
  resolve: generatePasswordChangeTokenResolver,
  type: () => String,
  params: [
    { name: "userID", type: () => String },
    { name: "userClassName", type: () => String }
  ]
});
var generatePasswordChangeToken = (userID, userClass = AdornisUser) => generatePasswordChangeTokenOp(userID, userClass._class);
var changePasswordWithToken = registerMutation({
  operationName: "changePasswordWithToken",
  resolve: changePasswordWithTokenResolver,
  type: () => Boolean,
  params: [
    { name: "token", type: () => String },
    { name: "newPassword", type: () => String },
    { name: "login", type: () => Boolean }
  ]
});
var changePassword = async (userId, password, userClass = AdornisUser) => {
  userClass.passwordValidator({ value: password, key: "password", target: null });
  const permissionToken = await generatePasswordChangeToken(userId)();
  await changePasswordWithToken(permissionToken, password)();
};
var checkPasswordChangeToken = registerMutation({
  operationName: "checkPasswordChangeToken",
  resolve: checkPasswordChangeTokenResolver,
  type: () => Boolean,
  params: [{ name: "token", type: () => String }]
});
var isUserPasswordSetOp = registerQuery({
  operationName: "isUserPasswordSet",
  resolve: isUserPasswordSetResolver,
  type: () => Boolean,
  params: [
    { name: "userID", type: () => String },
    { name: "userClass", type: () => String }
  ]
});
var isUserPasswordSet = (userID, userClass = AdornisUser) => isUserPasswordSetOp(userID, userClass._class);
var registerUserOp = registerMutation({
  operationName: "registerUser",
  resolve: registerUserResolver,
  type: () => String,
  params: [
    { name: "userObj", type: () => AdornisUser },
    { name: "password", type: () => String },
    { name: "loginAfterCreate", type: () => Boolean }
  ]
});
var registerUser = async ({
  username = "",
  email,
  password,
  additionalFields,
  loginAfterCreate = false,
  userClass = AdornisUser
}) => {
  if (password)
    userClass.passwordValidator({ value: password, key: "password", target: null });
  const user = new userClass({
    username,
    email,
    ...additionalFields
  });
  const id = await registerUserOp(user, password, loginAfterCreate)();
  if (loginAfterCreate)
    await _subscribeCurrentUser();
  return id;
};
var addRoleToUserOp = registerMutation({
  operationName: "addRoleToUser",
  resolve: addRoleToUserResolver,
  type: () => Boolean,
  params: [
    { name: "userClass", type: () => String },
    { name: "userID", type: () => String },
    { name: "roleName", type: () => String },
    { name: "contexts", type: () => [String] }
  ]
});
var addRoleToUser = (userID, roleName, contexts, userClass = AdornisUser) => addRoleToUserOp(userClass._class, userID, roleName, contexts);
var removeRoleFromUserOp = registerMutation({
  operationName: "removeRoleFromUser",
  resolve: removeRoleFromUserResolver,
  type: () => Boolean,
  params: [
    { name: "userClass", type: () => String },
    { name: "userID", type: () => String },
    { name: "roleName", type: () => String },
    { name: "contexts", type: () => [String] }
  ]
});
var removeRoleFromUser = (userID, roleName, contexts, userClass = AdornisUser) => removeRoleFromUserOp(userClass._class, userID, roleName, contexts);
export {
  AdornisUser,
  BaseUser,
  addRoleToUser,
  changePassword,
  changePasswordWithToken,
  checkPasswordChangeToken,
  downGradeSession,
  generatePasswordChangeToken,
  impersonateUser,
  isUserPasswordSet,
  loginWithPassword,
  loginWithToken,
  registerUser,
  removeRoleFromUser,
  stopImpersonationOfUser
};
